import { FC, memo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { useSnackbar } from "notistack"
import { Userbot, UserbotBatches } from "../../interfaces"
import { usePromoteClientMutation, useRolesQuery } from "../../store"

interface IProps {
  client: Userbot.FeedClient
  onClose: () => void
}

export const PromoteClientDialog: FC<IProps> = memo(
  function PromoteClientDialog({ client, onClose }) {
    const { enqueueSnackbar } = useSnackbar()
    const { data: roles } = useRolesQuery({ limit: 100 })
    const [promoteClient, { isLoading }] = usePromoteClientMutation()
    const [roleNames, setRoleNames] = useState<string[]>([])
    const [noRoles, setNoRoles] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Каскад ролей идёт от ролей по имени (roleNames — имена, дельта C §2).
    const activeRoleNames = (roles?.items ?? [])
      .filter((r) => r.isActive)
      .map((r) => r.name)

    const handleConfirm = async () => {
      setError(null)
      // Пустой выбор → поле не отправляется (каскад); чекбокс → явный [] («без
      // ролей», каскад не запускается); дельта C §2.
      const body: UserbotBatches.PromoteClientDto = noRoles
        ? { roleNames: [] }
        : roleNames.length > 0
          ? { roleNames }
          : {}
      try {
        await promoteClient({ id: client.id, body }).unwrap()
        enqueueSnackbar(
          "Клиент продвинут. Лимиты стартуют с прогревом от возраста авторизации",
          { variant: "success" },
        )
        onClose()
      } catch (e) {
        setError(getApiErrorMessage(e))
      }
    }

    return (
      <Modal
        disableBackdropClose
        title={`Продвинуть клиента «${client.name}»?`}
        onClose={onClose}
        element={
          <Stack spacing={2}>
            <Alert severity="warning">
              Роли мёртвой сессии невосстановимы. Пустой выбор = роли возьмутся
              от самой свежей сессии этого аккаунта с непустыми ролями.
            </Alert>
            <Autocomplete<string, true, false, false>
              multiple
              options={activeRoleNames}
              value={roleNames}
              disabled={noRoles}
              onChange={(_, v) => setRoleNames(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Роли (опционально)"
                  placeholder="Пусто — каскад от живого sibling"
                />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={noRoles}
                  onChange={(e) => setNoRoles(e.target.checked)}
                />
              }
              label="Назначить без ролей"
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={onClose}>Отмена</Button>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={handleConfirm}
              >
                Продвинуть
              </Button>
            </Box>
          </Stack>
        }
      />
    )
  },
)
